import {
  useOrderScanInfo,
  useOrderScanReceive,
} from "hooks/order-scan/useOrderScan";
import PropTypes from "prop-types";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useToasts } from "react-toast-notifications";
import "./BarcodeScannerModal.scss";
import { useTranslation } from "react-i18next";
import { Alert, Button, Modal, ModalBody, ModalFooter, Row } from "reactstrap";
import WarningNoteItem from "./WarningNoteItem";
import { Link, useSearchParams } from "react-router-dom";
import { useGetOrderNotes, useUpdateOrderCost } from "hooks";
import {
  formatOrderCostMoney,
  fromOrderCostThousandUnit,
  parseOrderCostMoney,
} from "helpers/orderCostHelper";
import TicketBarcodeScannerButton from "pages/TicketManagement/TicketBarcodeScannerButton";

export const LABEL_SCAN_CODE = {
  RECEIVE: "Quét nhận kiện",
  SEND: "Quét kết quả",
};

const STORAGE_KEY_MONEY = "scan_receive_money";

const shouldBlockScanAutoFocus = () => {
  if (document.querySelector(".modal.show")) {
    return true;
  }

  const activeElement = document.activeElement;

  return Boolean(activeElement?.closest?.(".modal"));
};

const BarcodeScannerModal = props => {
  const {
    orderData,
    orderDataInfo,
    setOrderData,
    setOrderDataInfo,
    allowFocusInput,
    isSendOrder,
    scan_id,
    transport_id,
    allowed_group_to_transfer,
    from_hub,
    to_hub,
    myHub,
    requireScanSession = true,
    registerFocusInput,
  } = props;

  const MODE_SCAN = {
    SCAN_CODE: "SCAN_CODE", //alt + z thì chuyển sang mode quét kiện
    SEARCH_INFO: "SEARCH_INFO", // alt + a thì chuyển sang mode tìm kiếm
  };

  const [searchParams, setSearchParams] = useSearchParams();
  const [labelScanCode, setLabelScanCode] = useState(LABEL_SCAN_CODE.RECEIVE);
  const [modeScan, setModeScan] = useState(MODE_SCAN.SCAN_CODE);
  const isScanSessionRequired = requireScanSession !== false;
  const canScanWithoutSession = !isScanSessionRequired || !!scan_id;
  const [warningNoteModal, setWarningNoteModal] = useState(false);
  const [orderNotesData, setOrderNotesData] = useState(null);
  const [currentOrderId, setCurrentOrderId] = useState(null);
  const [moneyValue, setMoneyValue] = useState(() => {
    try {
      return parseOrderCostMoney(localStorage.setItem(STORAGE_KEY_MONEY, moneyValue));
    } catch {
      return "";
    }
  });


  const { addToast } = useToasts();
  const { t } = useTranslation(["order"]);

  const { mutate: mutateUpdateOrderCost, isLoading: isUpdatingCost } =
  useUpdateOrderCost();

  const pendingScanContextRef = useRef({ barcode: "", previousOrder: null });
  const pendingSearchInfoContextRef = useRef({ barcode: "", previousOrder: null });

  const { mutate: mutateOrderScanReceive, isLoading: isLoadingScanReceivce } =
    useOrderScanReceive({
      onSuccess: data => {
        const { barcode, previousOrder } = pendingScanContextRef.current;

        setOrderData(data, { barcode, previousOrder });
        handleWarningNoteOrder(data);
      },
      onError: messError => {
        const { barcode, previousOrder } = pendingScanContextRef.current;

        setOrderData(messError, { barcode, previousOrder });
      },
    });

  const { refetch: refetchOrderNotes, isLoading: isLoadingOrderNotes } =
    useGetOrderNotes(
      { order_id: currentOrderId },
      data => {
        setOrderNotesData(data);
        setWarningNoteModal(true);
      },
      error => {
        console.error("Error fetching order notes:", error);
      }
    );
  const handleMoneyKeyDown = event => {
    if (event.key.toLowerCase() !== "enter") return;
    const orderId =
      modeScan === MODE_SCAN.SEARCH_INFO
        ? orderDataInfo?.id || orderDataInfo?.object?.id
        : orderData?.object?.id ||
          orderDataInfo?.id ||
          orderDataInfo?.object?.id;
    if (!orderId) {
      addToast(t("message.noOrderToUpdateCost"), {
        appearance: "warning",
        autoDismiss: true,
      });
      return;
    }
    const numericCost = fromOrderCostThousandUnit(moneyValue);
    mutateUpdateOrderCost({
      orderId,
      cost_value: numericCost,
    });
  };


  useEffect(() => {
    if (currentOrderId) {
      refetchOrderNotes();
    }
  }, [currentOrderId, refetchOrderNotes]);

  const handleWarningNoteOrder = data => {
    if (Boolean(data.object?.is_transfer_order)) {
      const isAnyHaveNote = data.object?.childs?.find(item =>
        Boolean(item.is_noted)
      );
      if (isAnyHaveNote) {
        setWarningNoteModal(true);
      }
    } else {
      if (data?.object?.is_noted && data?.object?.id) {
        setCurrentOrderId(data.object.id);
        setTimeout(() => {
          setWarningNoteModal(false);
          setOrderNotesData(null);
          setCurrentOrderId(null);
        }, 5000);
      }
    }
  };

  const { mutate: mutateOrderScanInfo, isLoading: isLoadingScanInfo } =
    useOrderScanInfo({
      onSuccess: data => {
        const { barcode, previousOrder } = pendingSearchInfoContextRef.current;
        setOrderDataInfo(data, { barcode, previousOrder });
      },
      onError: messError => {
        const { barcode, previousOrder } = pendingSearchInfoContextRef.current;
        setOrderDataInfo(messError, { barcode, previousOrder });
      },
    });

  const inputSearch = useRef(null);
  const inputMoneyRef = useRef(null);

  const clearOrder = () => {
    setOrderData(null);
    setOrderDataInfo(null);
    setOrderNotesData(null);
    setCurrentOrderId(null);
  };

  const handleMoneyBlur = () => {
    try {
      localStorage.setItem(STORAGE_KEY_MONEY, moneyValue);
    } catch { }
  };

  const handleKeyDown = event => {
    if (event.key.toLowerCase() === "enter") {
      const scannedBarcode = inputSearch.current?.value ?? "";
      pendingScanContextRef.current = {
        barcode: scannedBarcode,
        previousOrder: orderData,
      };
      clearOrder();
      switch (modeScan) {
        case MODE_SCAN.SCAN_CODE:
          if (isScanSessionRequired && !scan_id) {
            addToast("Vui lòng click button bắt đầu đợt để cho phép quét", {
              appearance: "error",
              autoDismiss: true,
            });
            return;
          }
          mutateOrderScanReceive({
            transport_id: transport_id,
            scan_id: scan_id || null,
            barcode: inputSearch.current.value,
            isSendOrder: isSendOrder,
            allowed_group_to_transfer: allowed_group_to_transfer ? 1 : 0,
            from_hub: from_hub?.id,
            to_hub: to_hub?.id,
            manager_hub_id: myHub?.id,
          });
          break;
        case MODE_SCAN.SEARCH_INFO:
          pendingSearchInfoContextRef.current = {
            barcode: scannedBarcode,
            previousOrder: orderDataInfo,
          };
          searchParams.delete("search");
          setSearchParams(searchParams);
          mutateOrderScanInfo({
            barcode: inputSearch.current.value,
          });
          break;
        default:
          break;
      }
      inputSearch.current.value = "";
      inputSearch.current.focus({ preventScroll: true });
    }
  };

  const handleScanBarcodeFromCamera = scannedBarcode => {
    const normalizedBarcode = String(scannedBarcode || "").trim();
    if (!normalizedBarcode) {
      return;
    }

    if (modeScan === MODE_SCAN.SEARCH_INFO) {
      pendingSearchInfoContextRef.current = {
        barcode: normalizedBarcode,
        previousOrder: orderDataInfo,
      };
    } else {
      pendingScanContextRef.current = {
        barcode: normalizedBarcode,
        previousOrder: orderData,
      };
    }

    clearOrder();

    switch (modeScan) {
      case MODE_SCAN.SCAN_CODE:
        if (isScanSessionRequired && !scan_id) {
          addToast("Vui lòng click button bắt đầu đợt để cho phép quét", {
            appearance: "error",
            autoDismiss: true,
          });
          return;
        }
        mutateOrderScanReceive({
          transport_id: transport_id,
          scan_id: scan_id || null,
          barcode: normalizedBarcode,
          isSendOrder: isSendOrder,
          allowed_group_to_transfer: allowed_group_to_transfer ? 1 : 0,
          from_hub: from_hub?.id,
          to_hub: to_hub?.id,
          manager_hub_id: myHub?.id,
        });
        break;
      case MODE_SCAN.SEARCH_INFO:
        searchParams.delete("search");
        setSearchParams(searchParams);
        mutateOrderScanInfo({
          barcode: normalizedBarcode,
        });
        break;
      default:
        break;
    }

    inputSearch.current.value = "";
    inputSearch.current.focus({ preventScroll: true });
  };

  useEffect(() => {
    let searchOrderCode = searchParams.get("search");
    if (searchOrderCode) {
      setModeScan(MODE_SCAN.SEARCH_INFO);
      inputSearch.current.value = searchOrderCode;
      pendingSearchInfoContextRef.current = {
        barcode: searchOrderCode,
        previousOrder: null,
      };
      mutateOrderScanInfo({
        barcode: searchOrderCode,
      });
    }
  }, [searchParams]);

  useEffect(() => {
    if (typeof registerFocusInput === "function") {
      registerFocusInput(() => {
        inputSearch.current?.focus({ preventScroll: true });
      });
    }
  }, [registerFocusInput]);

  useEffect(() => {
    if (!allowFocusInput || !scan_id || modeScan !== MODE_SCAN.SCAN_CODE) {
      return;
    }

    if (!canScanWithoutSession) {
      return;
    }

    if (shouldBlockScanAutoFocus()) {
      return;
    }

    inputSearch.current?.focus({ preventScroll: true });
  }, [allowFocusInput, scan_id, modeScan, canScanWithoutSession]);

  useEffect(() => {
    setLabelScanCode(
      isSendOrder ? LABEL_SCAN_CODE.SEND : LABEL_SCAN_CODE.RECEIVE
    );
    if (allowFocusInput && !shouldBlockScanAutoFocus()) {
      inputSearch.current.focus({ preventScroll: true });
    }

    const handleKeyPress = event => {
      if (shouldBlockScanAutoFocus()) {
        return;
      }

      if (event.altKey && event.code === "KeyA") {
        setModeScan(MODE_SCAN.SEARCH_INFO);
        event.preventDefault();
        setTimeout(() => {
          inputSearch.current?.focus({ preventScroll: true });
        }, 0);
      }

      if (event.altKey && event.code === "KeyZ") {
        setModeScan(MODE_SCAN.SCAN_CODE);
        event.preventDefault();
        setTimeout(() => {
          if (canScanWithoutSession) {
            inputSearch.current?.focus({ preventScroll: true });
          }
        }, 0);
      }

      if (event.altKey && event.code === "KeyQ") {
        event.preventDefault();
        const isInputSearchDisabled =
          isLoadingScanReceivce ||
          isLoadingScanInfo ||
          (modeScan === MODE_SCAN.SCAN_CODE && !canScanWithoutSession);

        if (document.activeElement === inputSearch.current) {
          inputMoneyRef.current?.focus({ preventScroll: true });
        } else if (document.activeElement === inputMoneyRef.current) {
          if (!isInputSearchDisabled) {
            inputSearch.current?.focus({ preventScroll: true });
          }
        } else {
          if (!isInputSearchDisabled) {
            inputSearch.current?.focus({ preventScroll: true });
          } else {
            inputMoneyRef.current?.focus({ preventScroll: true });
          }
        }
      }
    };

    // Attach the event listener when the component mounts
    document.addEventListener("keydown", handleKeyPress);

    // Clean up the event listener when the component unmounts
    return () => {
      document.removeEventListener("keydown", handleKeyPress);
    };
  }, [
    modeScan,
    scan_id,
    canScanWithoutSession,
    isLoadingScanReceivce,
    isLoadingScanInfo,
  ]);

  useEffect(() => {
    let intervalId = null;
    if (allowFocusInput) {
      intervalId = setInterval(() => {
        if (shouldBlockScanAutoFocus()) {
          return;
        }

        const selection = window.getSelection();
        const hasTextSelected = selection && selection.toString().trim().length > 0;
        if (hasTextSelected) return;
        if (document.activeElement === inputMoneyRef.current) return;
        inputSearch.current?.focus({ preventScroll: true });
      }, 500);
    } else {
      clearInterval(intervalId);
    }
    return () => {
      clearInterval(intervalId);
    };
  }, [allowFocusInput]);

  useEffect(() => {
    clearOrder();
  }, [modeScan]);

  const isTransferOrder = useMemo(() => {
    return Boolean(orderData?.object?.is_transfer_order);
  }, [orderData]);

  const handleCloseModal = () => {
    setWarningNoteModal(false);
    setOrderNotesData(null);
    setCurrentOrderId(null);
  };

  return (
    <div
      className="d-flex BarcodeScannerModal"
      style={{ flexDirection: "column", marginTop: "-16px" }}
    >
      <div className="search-box mb-md-2 me-2 d-flex gap-2 flex-wrap">
        <div
          className="d-flex flex-column gap-1"
          style={{ width: "30vw", minWidth: "276px" }}
        >
          <div className="fw-bold d-flex gap-2">
            <div className="title-scan">
              {modeScan === MODE_SCAN.SCAN_CODE ? (
                <div className="text-primary d-flex gap-1 align-items-center">
                  <i className={`bx bxs-circle text-primary`}></i>
                  <div className="label-scan"> {labelScanCode} </div>
                  <sup className="text-info " style={{ fontStyle: "italic" }}>
                    (ALT + A để sang mode Tìm kiếm)
                  </sup>{" "}
                </div>
              ) : (
                <div className="text-success d-flex gap-1 align-items-center">
                  <i className={`bx bxs-circle text-success `}></i>
                  <div className="label-scan"> Tìm kiếm </div>
                  <sup className="text-info " style={{ fontStyle: "italic" }}>
                    (ALT + Z để sang mode {labelScanCode})
                  </sup>
                </div>
              )}
            </div>
          </div>
          <div className="position-relative">
            <input
              disabled={
                isLoadingScanReceivce ||
                isLoadingScanInfo ||
                (modeScan === MODE_SCAN.SCAN_CODE && !canScanWithoutSession)
              }
              style={{ width: "100%"}}
              ref={inputSearch}
              type="search"
              onKeyDown={handleKeyDown}
              className="form-control"
              placeholder={`Nhập Barcode. Enter để ${modeScan === MODE_SCAN.SCAN_CODE ? labelScanCode : "Tìm kiếm"
                }`}
            />
            <i
              className="bx bxs-barcode search-icon"
              style={{ color: "#50a5f1" }}
            />
            <TicketBarcodeScannerButton handleResult={handleScanBarcodeFromCamera} />
          </div>
        </div>

        <div className="d-flex flex-column gap-1" style={{ width: "140px" }}>
          <div className="fw-bold d-flex gap-2">
            <div className="title-scan">
              <div className="text-warning d-flex gap-1 align-items-center">
                <i className={`bx bxs-circle text-warning`}></i>
                <div className="label-scan">Chi phí</div>
                <sup className="text-info" style={{ fontStyle: "italic" }}>
                  (ALT + Q)
                </sup>
              </div>
            </div>
          </div>

          <input
            ref={inputMoneyRef}
            type="text"
            inputMode="numeric"
            placeholder="Nhập số tiền"
            value={formatOrderCostMoney(moneyValue)}
            onChange={e => {
              setMoneyValue(parseOrderCostMoney(e.target.value));
            }}
            onBlur={handleMoneyBlur}
            onKeyDown={handleMoneyKeyDown}
            className="form-control"
            style={{ width: "150px", paddingLeft: "10px" }}
          />
        </div>
      </div>
      <Modal
        isOpen={warningNoteModal}
        autoFocus={true}
        toggle={handleCloseModal}
        centered
      >
        <div className="modal-content">
          <ModalBody style={{ padding: "16px" }}>
            <Alert
              color="warning"
              style={{ marginBottom: "12px", padding: "8px 12px" }}
            >
              {isTransferOrder
                ? "Có đơn con được lưu ý !"
                : "Đơn hàng được lưu ý !"}
            </Alert>
            {isTransferOrder ? (
              <div>
                {orderData?.object?.childs?.map(
                  item =>
                    item.is_noted && (
                      <div
                        key={item.code}
                        style={{
                          padding: "8px 12px",
                          marginBottom: "8px",
                          backgroundColor: "#f8f9fa",
                          borderRadius: "4px",
                        }}
                      >
                        <span>{item.code}</span>
                        <Link
                          to={`/manage-orders/${item?.order_id}`}
                          target="_blank"
                          style={{ marginLeft: "12px" }}
                        >
                          Xem chi tiết
                        </Link>
                      </div>
                    )
                )}
              </div>
            ) : (
              <>
                {isLoadingOrderNotes ? (
                  <div style={{ padding: "8px 0" }}>
                    Đang tải dữ liệu lưu ý...
                  </div>
                ) : (
                  <WarningNoteItem
                    reason={""}
                    noted_description={""}
                    notesData={orderNotesData}
                  />
                )}
              </>
            )}
          </ModalBody>
          <ModalFooter style={{ padding: "12px 16px" }}>
            <Button onClick={handleCloseModal}>Đóng</Button>
          </ModalFooter>
        </div>
      </Modal>
    </div>
  );
};

BarcodeScannerModal.propTypes = {
  transport_id: PropTypes.any,
  orderData: PropTypes.any,
  orderDataInfo: PropTypes.any,
  setOrderData: PropTypes.any,
  setOrderDataInfo: PropTypes.any,
  allowFocusInput: PropTypes.any,
  isSendOrder: PropTypes.bool,
  scan_id: PropTypes.any,
  requireScanSession: PropTypes.bool,
  registerFocusInput: PropTypes.func,
  allowed_group_to_transfer: PropTypes.bool,
  from_hub: PropTypes.any,
  to_hub: PropTypes.any,
  myHub: PropTypes.any,
};

export default BarcodeScannerModal;
